import { ROUTES } from '#/config/routes';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import {
  createDocumentNavLinksColumn,
  createTipHeader,
} from '#/modules/commonComponents/table/tableColumnHelpers';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { STOCK_RECEIPT_STATUS_FILTER_OPTIONS } from '#/modules/imsMgmt/receipts/shared/receiptStatus';
import { STOCK_RECEIPT_RECEIPT_DATE_TIP } from '#/modules/imsMgmt/receipts/stockReceipt/helper/receiptDateTip';
import {
  readReceiptDateRangeFilter,
  resolveReceiptDateFilterChipDisplay,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/receiptDateListFilter';
import {
  STOCK_RECEIPT_SOURCE_TYPE_FILTER_OPTIONS,
  getStockReceiptSourceTypeLabel,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/stockReceiptSource';
import { searchWarehouseFilterOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import { formatDateOrDash } from '#/utils/date';
import { formatDocumentCurrencyLabel } from '#/utils/documentCurrencyLabel';

const DATE_FILTER_PLACEHOLDER = '西元年/月/日';

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return value;
}

export function buildStockReceiptColumns({
  filters = {},
  onFilterChange,
  onReceiptDateRangeChange,
  warehouseLabelCache,
  canShowEdit,
}) {
  return [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.stockReceiptDetail(row.id),
      getEditPath: (row) => ROUTES.stockReceiptEdit(row.id),
      canShowEdit,
    }),
    {
      id: 'receiptNo',
      header: '進貨單號',
      accessorKey: 'receiptNo',
      accessorFn: (row) => displayDash(row.receiptNo),
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'receiptNo',
        label: '進貨單號',
        filterValue: filters.receiptNo,
        onChange: (value) => onFilterChange('receiptNo', value),
        placeholder: '搜尋進貨單號',
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorFn: (row) => row.status,
      cell: ({ row }) => (
        row.original.status
          ? <DocumentStatusBadge status={row.original.status} size="sm" />
          : '-'
      ),
      size: 96,
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: STOCK_RECEIPT_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
    },
    {
      id: 'sourceType',
      header: '來源',
      accessorKey: 'sourceType',
      accessorFn: (row) => getStockReceiptSourceTypeLabel(row.sourceType),
      cell: ({ row }) => getStockReceiptSourceTypeLabel(row.original.sourceType),
      size: 96,
      enableSorting: false,
      enableColumnFilter: true,
      meta: createFixedMultiFilterMeta({
        key: 'sourceType',
        label: '來源',
        options: STOCK_RECEIPT_SOURCE_TYPE_FILTER_OPTIONS,
        filterValue: filters.sourceType,
        onChange: (value) => onFilterChange('sourceType', value),
      }),
    },
    {
      id: 'warehouse',
      header: '倉別',
      accessorFn: (row) => row.warehouse?.name ?? '-',
      size: 140,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'warehouseId',
          label: '倉別',
          searchOptions: searchWarehouseFilterOptions,
          filterValue: filters.warehouseId,
          onChange: (value) => onFilterChange('warehouseId', value),
          resolveFilterDisplay: (values) => resolveFromLabelCache(warehouseLabelCache, values),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(warehouseLabelCache, options, values);
          },
        }),
      },
    },
    {
      id: 'receiptDate',
      header: createTipHeader('製造商開單日', STOCK_RECEIPT_RECEIPT_DATE_TIP),
      accessorKey: 'receiptDate',
      accessorFn: (row) => formatDateOrDash(row.receiptDate),
      size: 148,
      minSize: 148,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'receiptDateFrom',
        label: '製造商開單日',
        mode: 'range',
        filterValue: readReceiptDateRangeFilter(filters),
        onChange: onReceiptDateRangeChange,
        placeholder: DATE_FILTER_PLACEHOLDER,
        resolveFilterDisplay: resolveReceiptDateFilterChipDisplay,
      }),
    },
    {
      id: 'currencyCode',
      header: '幣別',
      accessorFn: (row) => formatDocumentCurrencyLabel(row),
      size: 160,
      minSize: 140,
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      id: 'supplier',
      header: '供應商',
      accessorFn: (row) => row.supplier?.name ?? row.supplier?.code ?? '-',
      size: 140,
      enableSorting: false,
      enableColumnFilter: false,
      meta: { cellClassName: 'td_left' },
    },
    {
      id: 'issueReason',
      header: '事由',
      accessorKey: 'issueReason',
      accessorFn: (row) => displayDash(row.issueReason),
      size: 160,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'issueReason',
          label: '事由',
          filterValue: filters.issueReason,
          onChange: (value) => onFilterChange('issueReason', value),
          placeholder: '搜尋事由',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original.issueReason)} />
      ),
    },
    {
      id: 'remark',
      header: '備註',
      accessorKey: 'remark',
      accessorFn: (row) => displayDash(row.remark),
      size: 160,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '事由',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original.remark)} />
      ),
    },
  ];
}
